import { useCallback, useMemo, useReducer } from 'react';
import { useTranslation } from 'react-i18next';
import { useMemorizedCacheTag } from '../../hooks/useMemorizedCacheTag';
import { loadingReducer, loadingReducerInitialState } from '../../reducer/loadingReducer';
import {
  RoomFlag,
  RoomInput,
  useAssignRoomsToBuildingMutation,
  useCreateRoomsMutation,
  useDeleteRoomsMutation,
  useRoomsListQuery,
  useSetRoomsFlagMutation,
  useUpdateRoomsMutation,
} from '../../types/planung-graphql-client-defs';

export type RoomsFormInitialValuesType = {
  uuid?: string;
  name: string;
  position?: string | null;
  roomNumber?: string | null;
  description?: string | null;
  canBeLeased: boolean;
  capacity?: number | null;
  chairs?: number | null;
  tables?: number | null;
  size?: number | null;
  otherInventory?: string | null;
  classroom: boolean;
  barrierFree: boolean;
  canBeDarkened: boolean;
  hasProjector: boolean;
  hasScreen: boolean;
  hasMediaCabinet: boolean;
  hasPiano: boolean;
  building?: { uuid: string; name: string } | null;
  responsiblePersons: { uuid: string; displayNameShort: string }[];
};

export const booleanKeys: string[] = [
  'barrierFree',
  'hasProjector',
  'canBeLeased',
  'hasPiano',
  'hasScreen',
  'hasMediaCabinet',
  'classroom',
  'canBeDarkened',
];

type CheckboxTypes = (typeof booleanKeys)[number];

const flagByCheckbox: Record<CheckboxTypes, RoomFlag> = {
  barrierFree: RoomFlag.BarrierFree,
  hasProjector: RoomFlag.HasProjector,
  canBeLeased: RoomFlag.CanBeLeased,
  hasPiano: RoomFlag.HasPiano,
  hasScreen: RoomFlag.HasScreen,
  hasMediaCabinet: RoomFlag.HasMediaCabinet,
  classroom: RoomFlag.Classroom,
  canBeDarkened: RoomFlag.CanBeDarkened,
};

/** Das Backend erwartet immer den vollständigen Stand eines Raums. */
const formToInput = (values: RoomsFormInitialValuesType): RoomInput => ({
  name: values.name,
  buildingUuid: values.building?.uuid ?? null,
  position: values.position,
  roomNumber: values.roomNumber,
  description: values.description,
  capacity: values.capacity,
  chairs: values.chairs,
  tables: values.tables,
  size: values.size,
  otherInventory: values.otherInventory,
  canBeLeased: values.canBeLeased,
  barrierFree: values.barrierFree,
  classroom: values.classroom,
  canBeDarkened: values.canBeDarkened,
  hasProjector: values.hasProjector,
  hasScreen: values.hasScreen,
  hasMediaCabinet: values.hasMediaCabinet,
  hasPiano: values.hasPiano,
  responsiblePersonUuids: (values.responsiblePersons ?? []).filter((p) => p?.uuid).map((p) => p.uuid),
});

export const useRooms = () => {
  const context = useMemorizedCacheTag('ROOMS');
  const { t } = useTranslation();

  const [loadingState, dispatch] = useReducer(loadingReducer, loadingReducerInitialState);

  const [, create] = useCreateRoomsMutation();
  const [, update] = useUpdateRoomsMutation();
  const [, remove] = useDeleteRoomsMutation();
  const [, setFlag] = useSetRoomsFlagMutation();
  const [, assignBuilding] = useAssignRoomsToBuildingMutation();

  const [{ data }] = useRoomsListQuery({
    context,
  });

  const getFormData = useCallback(
    (roomUuid?: string | null): RoomsFormInitialValuesType => {
      const currentRoom = data?.rooms.find((r) => r.uuid === roomUuid);

      return {
        roomNumber: currentRoom?.roomNumber,
        description: currentRoom?.description,
        capacity: currentRoom?.capacity,
        chairs: currentRoom?.chairs,
        tables: currentRoom?.tables,
        building: currentRoom?.building,
        classroom: currentRoom?.classroom ?? true,
        canBeDarkened: currentRoom?.canBeDarkened ?? false,
        hasMediaCabinet: currentRoom?.hasMediaCabinet ?? false,
        hasPiano: currentRoom?.hasPiano ?? false,
        hasProjector: currentRoom?.hasProjector ?? false,
        hasScreen: currentRoom?.hasScreen ?? false,
        barrierFree: currentRoom?.barrierFree ?? false,
        name: currentRoom?.name ?? '',
        canBeLeased: currentRoom?.canBeLeased ?? false,
        position: currentRoom?.position,
        otherInventory: currentRoom?.otherInventory,
        size: currentRoom?.size,
        responsiblePersons:
          currentRoom?.responsiblePersonUuids.map((personUuid) => ({
            uuid: personUuid,
            // Der Raum nennt nur die uuids; der Name kommt aus der Personenliste derselben Abfrage.
            displayNameShort: data?.people.find((person) => person.uuid === personUuid)?.displayNameShort ?? '',
          })) ?? [],
      };
    },
    [data],
  );

  const roomData = useMemo(() => {
    // to remove null values; to make the filters work
    // Die Tabelle ist nach Raumnummer sortiert (Räume ohne Nummer hinten), wie vor der Umstellung der
    // Abfrage; das Backend liefert Räume nach Name.
    const byRoomNumber = [...(data?.rooms ?? [])].sort((a, b) => {
      if (a.roomNumber == null || b.roomNumber == null)
        return (a.roomNumber == null ? 1 : 0) - (b.roomNumber == null ? 1 : 0);
      return a.roomNumber < b.roomNumber ? -1 : a.roomNumber > b.roomNumber ? 1 : 0;
    });
    const rooms = data && byRoomNumber.map((room) => ({ ...room, ...getFormData(room.uuid) }));
    if (data && rooms) data.rooms = rooms;
    return data;
  }, [data, getFormData]);

  const createRoom = async (values: RoomsFormInitialValuesType) => {
    return await create({ input: formToInput(values) }, context);
  };

  const updateRoom = async (values: RoomsFormInitialValuesType, roomUuid: string) => {
    dispatch({ type: 'SET_LOADING', uuids: [roomUuid] });
    const res = await update({ uuid: roomUuid, input: formToInput(values) }, context);
    dispatch({ type: 'REMOVE_LOADING', uuids: [roomUuid] });
    return res;
  };

  const duplicateRooms = async (uuids: string[]) => {
    dispatch({ type: 'SET_LOADING', uuids: uuids });
    const roomData = data?.rooms
      .filter((r) => uuids.includes(r.uuid))
      .map((r) => {
        return getFormData(r.uuid);
      });
    const createResponses = roomData?.map((data) => createRoom(data)) ?? [];
    dispatch({ type: 'REMOVE_LOADING', uuids: uuids });
    return Promise.all(createResponses);
  };

  const deleteRooms = async (uuids: string[]) => {
    dispatch({ type: 'SET_LOADING', uuids: uuids });
    const res = await remove({ uuids: uuids }, context);
    dispatch({ type: 'REMOVE_LOADING', uuids: uuids });
    return res;
  };

  const updateCheckboxes = async ({ type, uuids, value }: { type: CheckboxTypes; uuids: string[]; value: boolean }) => {
    dispatch({ type: 'SET_LOADING', uuids: uuids });
    const res = await setFlag({ uuids, flag: flagByCheckbox[type], value }, context);
    dispatch({ type: 'REMOVE_LOADING', uuids: uuids });
    return res;
  };

  const getBooleanLabelKey = (type: CheckboxTypes) => {
    const data: Record<CheckboxTypes, string> = {
      classroom: t('rooms.isClassroom'),
      canBeLeased: t('rooms.canBeLeased'),
      barrierFree: t('rooms.isBarrierFree'),
      canBeDarkened: t('rooms.canBeDarkened'),
      hasProjector: t('rooms.hasProjector'),
      hasScreen: t('rooms.hasScreen'),
      hasMediaCabinet: t('rooms.hasMediaCabinet'),
      hasPiano: t('rooms.hasPiano'),
    };

    return data[type];
  };

  const assignToBuilding = async (uuids: string[], buildingUuid: string) => {
    dispatch({ type: 'SET_LOADING', uuids: uuids });
    const res = await assignBuilding({ uuids, buildingUuid }, context);
    dispatch({ type: 'REMOVE_LOADING', uuids: uuids });
    return res;
  };

  const detachFromBuilding = async (uuids: string[]) => {
    dispatch({ type: 'SET_LOADING', uuids: uuids });
    const res = await assignBuilding({ uuids, buildingUuid: null }, context);
    dispatch({ type: 'REMOVE_LOADING', uuids: uuids });
    return res;
  };

  return {
    createRoom,
    updateRoom,
    deleteRooms,
    getFormData,
    roomData,
    updateCheckboxes,
    loadingState,
    getBooleanLabelKey,
    duplicateRooms,
    assignToBuilding,
    detachFromBuilding,
  };
};
